import React, { type ReactNode } from 'react'
import { BrowserRouter, useLocation, useRoutes } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { MsalProvider } from '@azure/msal-react'
import { microsoftAuthClient } from './auth/microsoft'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { DashboardProvider } from './contexts/DashboardContext'
import { ConnectionsProvider } from './contexts/ConnectionsContext'
import { DataModelsProvider } from './contexts/DataModelsContext'
import { WorkspaceEventsProvider } from './contexts/WorkspaceEventsContext'
import { FluentProvider, webLightTheme, webDarkTheme, Theme } from '@fluentui/react-components'
import { useTheme } from './contexts/ThemeContext'
import { useAuth } from './contexts/AuthContext'
import ImpersonationBanner, { IMPERSONATION_BANNER_HEIGHT } from './components/ImpersonationBanner'
import { routes } from './routes'
import './App.css'
import { ErrorReportingProvider } from './contexts/ErrorReportingContext'
import AppErrorBoundary from './components/AppErrorBoundary'
import { isEmbeddedDashboardPath } from './routes/publicRoutes'

// Router component that uses the routes configuration
const AppRoutes: React.FC = () => {
  const routing = useRoutes(routes);
  return routing;
};

/**
 * Domain state is scoped to the effective signed-in identity.
 *
 * Changing between administrator and impersonated subject remounts every
 * user-data provider, clearing stale data before loading with the new token.
 */
const IdentityScopedProviders: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const identityKey = user
    ? `${user.id}:${user.impersonation?.sessionId ?? 'normal'}`
    : 'anonymous';

  return (
    <DashboardProvider key={identityKey}>
      <ConnectionsProvider>
        <DataModelsProvider>
          <WorkspaceEventsProvider>
            {children}
          </WorkspaceEventsProvider>
        </DataModelsProvider>
      </ConnectionsProvider>
    </DashboardProvider>
  );
};

// Create a wrapper component to access theme context inside FluentProvider
const AppContent: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { isImpersonating } = useAuth();

  // Use Fluent UI themes based on our theme context
  const fluentTheme: Theme = isDarkMode ? webDarkTheme : webLightTheme;

  return (
    <FluentProvider theme={fluentTheme}>
      <ErrorReportingProvider>
        <AppErrorBoundary>
          <IdentityScopedProviders>
            <ImpersonationBanner />
            <div
              className="App"
              style={isImpersonating ? { paddingTop: IMPERSONATION_BANNER_HEIGHT } : undefined}
            >
              <AppRoutes />
            </div>
          </IdentityScopedProviders>
        </AppErrorBoundary>
      </ErrorReportingProvider>
    </FluentProvider>
  );
};

const EmbeddedAppContent: React.FC = () => {
  const { isDarkMode } = useTheme();
  const fluentTheme: Theme = isDarkMode ? webDarkTheme : webLightTheme;

  return (
    <FluentProvider theme={fluentTheme}>
      <ErrorReportingProvider>
        <AppErrorBoundary>
          <div className="App">
            <AppRoutes />
          </div>
        </AppErrorBoundary>
      </ErrorReportingProvider>
    </FluentProvider>
  );
};

const RoutedProviders: React.FC = () => {
  const { pathname } = useLocation();

  if (isEmbeddedDashboardPath(pathname)) {
    return <EmbeddedAppContent />;
  }

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  if (!googleClientId) {
    console.warn('[App] VITE_GOOGLE_CLIENT_ID is not set — Google login will not work.');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <MsalProvider instance={microsoftAuthClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </MsalProvider>
    </GoogleOAuthProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <RoutedProviders />
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App